import Vue from 'vue'
import EventBus from "@/utils/EventBus";
import Client from "@/utils/Client";
import Events from "@/utils/Events";
import Rpc from "@/Rpc";
import RpcUnauthorizedException from "@/server/com/silkplm/engine/rpc/exceptions/RpcUnauthorizedException";
import ServerEvents from "@/server/ServerEvents";
import DialogManager from "@/utils/DialogManager";
import _ from "lodash";
import Perms, {
    BUSINESS_OBJECTS_OFFSET,
    FORMS_LIST_SUB_CODE,
    FORMS_READ_SUB_CODE,
    FORMS_SUB_CODE_MAX_VALUE, FORMS_WRITE_SUB_CODE
} from "@/server/Perms";
import RpcNoPermissionsException from "@/server/com/silkplm/engine/rpc/exceptions/RpcNoPermissionsException";
import ConfigManager from "@/utils/ConfigManager";
import InputLock from "@/utils/InputLock";
import FormsLoader from "@/components/forms/FormsLoader";
import fixCookies from "@/mobile/MobileCookies";
import {connectInvitationToContact} from "@/invitations/invites";
import {ALL_MODULES} from "@/utils/Modules";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import RegionsConfig from "@/server/com/silkplm/rpc/dto/RegionsConfig";
import {loadInterfaceLangList} from "@/utils/TranslationDispatcher";
import dialogManager from "@/utils/DialogManager";
import Currency from "@/server/com/silkplm/model/plm/dictionaries/Currency";
import CacheManager from "@/components/cacheManager/CacheManager";
import {sendEvent} from "@/utils/GEvents";
import RpcClass from "@/server/RpcClass";

const request = require('superagent');

export let SUB_TYPE_ENTERPRISE = "enterprise";
export let SUB_TYPE_OPEN = "open";

export let USER_SUB_STATUS_FREE = "free";
export let USER_SUB_STATUS_UNPAID = "unpaid";
export let USER_SUB_STATUS_NOTICE = "unpaid-notice";
export let USER_SUB_STATUS_PAID = "paid";

export let SILKPLM_STANDARD = "SILKPLM-STANDARD";
export let SILKPLM_DESIGNER ="SILKPLM-DESIGNER";
export let SILKPLM_FREE = "SILKPLM-FREE";

function setIntervalLight(callback, delay) {
    setTimeout(async function () {
        try {
            await callback();
        } finally {
            setIntervalLight(callback, delay);
        }
    }, delay);
}

class _SessionManager {
    constructor() {
        this.windowUUID = "";
        this.permissionCodes = [];

        this.offlineMode = false;

        // set to tru only once
        this.freshlyCreatedOrg = false;

        this.userOnTabConfig = new Map();
        this.unreadMessages = false;

        /** @type {string|null} */
        this.login = null;

        /** @type {ApplicationUser|null} */
        this.applicationUser = null;
        this.router = null;

        this.organizationSubscriptionType = SUB_TYPE_ENTERPRISE;
        this.userSubscriptionStatus = USER_SUB_STATUS_PAID;
        this.userSubscriptionType = SILKPLM_STANDARD;

        this.featureFlags = [];
        this.defaultCustomerLanguageCode = 'en';

        this.featureFlagsSet = {};
        this.systemLevelFeatureFlagsSet = {};

        this.modulesSet = {};
        this.permissions = [];
        this.permissionsByCode = {};
        this.permissionsByName = {};
        this.missingPermissionsWarnings = {};

        this.rebooting = false;
        this.refreshing = false;
        this.refreshAgain = false;

        this.isAdmin = false;
        this.forms = [];
        this.publicSessionId = "";

        this.regionsConfig = new RegionsConfig().withRegions([]).withAllLanguages([]).withEnabledLanguages([]);
        this.regionsByCode = {};

        /** @type {ConfigManager} */
        this.configManager = null;

        EventBus.$on(ServerEvents.subscriptionStatusChanged, async()=>{
            await this.refreshSession();
            let userData = await Rpc.Session.userInfo();
            if((this.userSubscriptionType === SILKPLM_FREE && userData.userSubscriptionType !== SILKPLM_FREE) ||
                (this.userSubscriptionType === SILKPLM_DESIGNER && userData.userSubscriptionType === SILKPLM_STANDARD)){
                if(userData.subscriptionStatus === USER_SUB_STATUS_PAID){
                    this.userSubscriptionStatus = userData.subscriptionStatus;
                    this.userSubscriptionType = userData.userSubscriptionType;
                    DialogManager.info($tr("sub.upgraded.title", "Subscription upgrade"),
                        $tr("sub.upgraded.body", "Your subscription has been upgraded.")).then();
                    await FormsLoader.reload();
                    EventBus.$emit(Events.menuUpdated, null);
                    EventBus.$emit(Events.resetSubInfo, null);

                    return;
                }
            }

            this.userSubscriptionStatus = userData.subscriptionStatus;
            this.userSubscriptionType = userData.userSubscriptionType;
            await this.closeSession($tr("sub-changed-msg", "Your subscription has been changed, please reload page"));
        });

        EventBus.$on(Events.serverBroken, async () => {
            await this.closeSession($tr("connection-broken-msg", "Connection to server broken, please reload page"));
        });
        EventBus.$on(Events.serverRebooting, async () => {
            if (!this.rebooting) {
                this.rebooting = true;
                let isDevEnv = this.isDev();

                if(!isDevEnv){
                    setTimeout(async () => {
                        await this.signOut();
                        location.reload();
                    }, "2000");
                }

                await this.showMaintenanceView()

                // DialogManager.openPendingDialog("Server Rebooting", "Please wait until server is rebooted. You will be able to continue your work in several seconds");
            }
        });
        EventBus.$on(Events.serverActive, async () => {
            if (this.rebooting) {
                this.rebooting = false;
                this.hideMaintenanceView();
                // DialogManager.closePendingDialog();
            }
        });

        EventBus.$on(Events.sessionBroken, async () => {
            await this.closeSession("Your session has been terminated. You will be logged off in 5s")
        });
        EventBus.$on(Events.rpcException, async (exception) => {
            if (exception instanceof RpcUnauthorizedException) {
                let userData = await Rpc.Session.userInfo();
                if (!userData || (!userData.applicationUser && !userData.isAdmin)) {
                    await this.closeSession("Your session has been closed by administrator. You need to sign-in again. You will be redirected to the login page in ${countDown}s");
                }
            } else if (exception instanceof RpcNoPermissionsException) {
                if (this.missingPermissionsWarnings.hasOwnProperty(exception.errorMessage)) {
                    return;
                }
                this.missingPermissionsWarnings[exception.errorMessage] = true;
                await DialogManager.info("Missing permissions", "One or more server calls was a blocked due to lack of permission:\n'" + exception.errorMessage + "'");
            }
        });
        EventBus.$on(ServerEvents.sessionTerminated, async () => {
            await this.closeSession("Your session has been closed by administrator. You need to sign-in again. You will be redirected to the login page in ${countDown}s");
        });
        EventBus.$on(ServerEvents.sessionTimeout, async () => {
            await this.closeSession("Your session has been time-outed. You will be redirected to the sign-in page in ${countDown}s")
        });
        EventBus.$on(ServerEvents.shutdownMessage, async () => {
            await this.sendShutdownMessage();
        });
        EventBus.$on(ServerEvents.permissionsChanged, async (permissions) => {
            await this.setPermissions(permissions.codes);
        });
        EventBus.$on(ServerEvents.sessionChanged, async () => {
            Log.trace("refresh session request received")
            await this.refreshSession();
        });
        let that = this;
        setIntervalLight(() => that.refreshSession(), 60 * 1000); //60 * 60 * 1000);

        this.timestamp = null;
        let host = window.location.hostname.toLowerCase();
        if (host.includes("cert") || host.includes("silkplm") || host.includes("demo")) {
            setIntervalLight(() => that.checkVersion(), 15 * 1000); //60 * 60 * 1000);
        }
        // else {
        //     setTimeout(()=>{
        //         EventBus.$emit(Events.versionAvailable);
        //     }, 5000);
        // }
    }

    async showMaintenanceView() {
        const MaintenanceViewClass = Vue.extend({
            render(h) {
                return h('MaintenanceView', {});
            }
        });
        const instance = new MaintenanceViewClass();
        instance.$mount();
        document.body.appendChild(instance.$el);
        this.maintenanceViewInstance = instance;
        document.body.style.overflow = 'hidden';
    }

    hideMaintenanceView() {
        if (this.maintenanceViewInstance) {
            document.body.removeChild(this.maintenanceViewInstance.$el);
            this.maintenanceViewInstance.$destroy();
            this.maintenanceViewInstance = null;
            document.body.style.overflow = '';
        }
    }

    async sendShutdownMessage() {

        await Rpc.Session.sendShutdownMessage()

        await DialogManager.info($tr("application.shutdown.message.header", "Application update"),
            $tr("application.shutdown.message.main", "An application update has been uploaded, you are about to be logged out."));
    }

    async closeSession(msg) {
        try {
            let dialog = DialogManager.loggedOff("Session expired", msg, 10);
            Rpc.Session.signOut().then(() => {
                dialog.enableButton()
            }).catch(() => {
                dialog.enableButton()
            });

            this.login = null;
            this.applicationUser = null;
            localStorage.removeItem('RpcClass.csrf');
            if (window['__rt_module'] !== undefined) {
                delete window['__rt_module'];
            }

            Client.lockDuringNextRequest();
        } catch (e) {
            Log.warn(e);
        }
        setTimeout(() => window.location.href = "/dashboard", 5000);
    }

    async checkVersion() {
        request.get(
            "/dashboard/timestamp.txt",
            (error, res) => {
                try {
                    if (res.statusCode, 200) {
                        // console.log(res);
                        if (this.timestamp) {
                            if (this.timestamp !== res.body) {
                                EventBus.$emit(Events.versionAvailable);
                                this.timestamp = res.body;
                            }
                        } else {
                            this.timestamp = res.body;
                        }
                    }
                } catch (err) {
                }
            });
    }

    async refreshSession() {
        if (this.refreshing) {
            this.refreshAgain = true;
            return;
        }

        this.refreshing = true;
        try {
            if (this.isLoggedIn()) {
                await Rpc.Session.keepAlive();
            }
        } catch (e) {
            if (e instanceof RpcUnauthorizedException) {
                window.location = "/dashboard";
            } else {
                throw e;
            }
        } finally {
            this.refreshing = false;
            if (this.refreshAgain) {
                this.refreshAgain = false;
                setTimeout(() => this.refreshSession().then(() => {
                }));
            }
        }
    }

    isLoggedIn() {
        return this.login !== undefined && this.login !== null && this.login !== '';
    }

    async signOut() {
        this.login = null;
        this.applicationUser = null;
        localStorage.removeItem('RpcClass.csrf');
        RpcClass.sessionCookie = "";

        Client.lockDuringNextRequest();
        await Rpc.Session.signOut();
        if (window['__rt_module'] !== undefined) {
            delete window['__rt_module'];
        }
        if (window.isCordova) {
            if (navigator.app) {
                navigator.app.exitApp();
            } else if (navigator.device) {
                navigator.device.exitApp();
            } else {
                console.log("Nie można zamknąć aplikacji");
            }
        } else {
            EventBus.$emit(Events.userSignedOut);
        }
    }

    inPrerenderMode() {
        if (window['PRERENDER_INJECTED'] !== undefined) {
            return true;
        }
        return false;
    }

    async initialize(adminModeLogout, userData) {
        if (!this.inPrerenderMode()) {
            if (adminModeLogout) {
                await Rpc.Session.signOut();
            }

            if(!userData) {
                userData = await Rpc.Session.userInfo();
            }
            this.organizationSetupProgress = userData.organizationSetupProgress;

            if (userData.applicationUser) {
                if (!RpcClass.csrfToken) {
                    RpcClass.csrfToken = userData.csrfToken;
                }
                if(userData.organizationSetupProgress === "complete") {
                    await this.installRuntimeCode();
                }
            }
            this.windowUUID = await Rpc.Session.getWindowUUID();
            window["_windowUUID"] = this.windowUUID;

            if(userData.organizationSetupProgress === "complete") {
                await this.setUserInfo(userData);
                if (userData.applicationUser) {
                    await this.postSignIn();
                }
            }
        }
        window['backendVersion'] = await Rpc.Interactions.backendVersion();
    }

    // after session is set, before app is active
    async postSignIn() {
        Rpc.Interactions.event("app-started", {}).then();
        sendEvent("app-started");

        this.configManager = new ConfigManager();
        // await new Promise(r=> setTimeout(r, 5000));
        await this.configManager.preload();
        // sendEvent("config-preloaded");
        RpcClass.EventBus.$emit("config-preloaded");

        if (!this.forms.length) {
            await this.preSignIn();
        }
        await SessionManager.loadUserSettings();

        this.interfaceLanguages = await loadInterfaceLangList();
        if(this.interfaceLanguages[AppTranslations.selectedLang] == null){
            if(!DialogManager.dialogManagerIsReady()){
                AppTranslations.switchLang(SessionManager.defaultInterfaceLanguageCode)
            } else {
                DialogManager.loggedOff($tr("sign-in.lang-switch", "Language switch",),
                    $tr("sign-in.lang-switch-msg", "Selected language is not supported in your organization, we will switch application to the organization default language now."),
                    5);
                setTimeout(() => {
                    AppTranslations.switchLang(SessionManager.defaultInterfaceLanguageCode);
                }, 5000);
            }
        }
        Log.info("SignIn complete");
    }

    getRegionByCode(code) {
        return this.regionsByCode[code];
    }

    async workspaceCreated() {
        await connectInvitationToContact();
    }

    async preSignIn(freshlyCreatedOrg = false) {
        this.freshlyCreatedOrg = freshlyCreatedOrg;
        this.regionsConfig = await Rpc.Regions.loadRegionsConfig();
        let currencies = await Currency.list();

        let regionsConf = this.regionsConfig;
        this.regionsByCode['def'] = {
            lang: null,
            region: null,
            type: 'default',
            code: 'def',
            label: 'Default',
            flag: '',
            language: '',
            order: 0,
            currencyRef: null
        };

        let langsByCode = {};
        for (let lang of regionsConf.enabledLanguages) {
            langsByCode[lang.code] = lang;
            this.regionsByCode[lang.code] = {
                lang: lang,
                region: null,
                type: 'lang',
                code: lang.code,
                label: lang.enName + " - base translation",
                flag: lang.code.toLowerCase(),
                language: lang.code,
                order: 1,
            };
        }

        for (let reg of regionsConf.regions) {
            let lang = langsByCode[reg.baseLanguage];

            this.regionsByCode[reg.baseLanguage].currencyRef = reg.currency ? currencies.find(c=>c.id === reg.currency): null;

            this.regionsByCode["r_" + reg.fixedSymbol] = {
                lang: lang,
                region: reg,
                type: 'region',
                code: "r_" + reg.fixedSymbol,
                label: (lang?.enName ?? 'Default') + " - Region: " + reg.name + "",
                flag: (reg.countryCode || reg.baseLanguage)?.toLowerCase() || 'def',
                language: reg.baseLanguage,
                currencyRef: reg.currency ? currencies.find(c=>c.id === reg.currency): null,
                order: 2,
            };
        }
        this.forms = await FormsLoader.load();
        this.cacheManager = CacheManager;
        this.cacheManager.reset();
    }

    moduleEnabled(module) {
        return (this.modulesSet[module] ?? false) || (module[module] && this.modulesSet[module[module]]);
    }

    anyModuleEnabled(modulesSet) {
        for (let m in modulesSet) {
            if (this.modulesSet[m] || (modulesSet[m] && this.modulesSet[modulesSet[m]])) {
                return true;
            }
        }
        return false;
    }

    async loadUserSettings() {
        let settings = await Rpc.Session.loadUserSettings();
        if (settings.streamingDataLoad === "Default") {
            this.featureFlagsSet[FeatureFlag.STREAMING_LISTS] = this.systemLevelFeatureFlagsSet[FeatureFlag.STREAMING_LISTS];
        } else if (settings.streamingDataLoad === "On") {
            this.featureFlagsSet[FeatureFlag.STREAMING_LISTS] = true;
        } else {
            this.featureFlagsSet[FeatureFlag.STREAMING_LISTS] = false;
        }
    }

    isEnterprise(){
        return this.organizationSubscriptionType === SUB_TYPE_ENTERPRISE;
    }
    isOpen(){
       return this.organizationSubscriptionType === SUB_TYPE_OPEN;
    }

    hasAccessToDesignerFeatures(){
        if(this.isEnterprise()){
            return true;
        }
        return this.userSubscriptionStatus !== USER_SUB_STATUS_FREE &&
            this.userSubscriptionStatus !== USER_SUB_STATUS_NOTICE &&
            this.userSubscriptionStatus !== USER_SUB_STATUS_UNPAID;
    }

    hasAccessToStandardFeatures() {
        return this.hasAccessToDesignerFeatures() && this.userSubscriptionType === SILKPLM_STANDARD;
    }

    /**
     * @param appUser {UserInfo}
     */
    setUserInfo(userInfo) {
        if (userInfo && (userInfo.isAdmin || userInfo.applicationUser)) {
            this.organizationSetupProgress = userInfo.organizationSetupProgress;

            this.organizationSubscriptionType = userInfo.organizationSubscriptionType;
            this.userSubscriptionStatus = userInfo.subscriptionStatus;
            this.userSubscriptionType = userInfo.userSubscriptionType;
            EventBus.$emit(Events.resetSubInfo, null);

            Log.info(`Subscription: ${this.organizationSubscriptionType} | ${this.userSubscriptionStatus} | ${this.userSubscriptionType}`)

            this.defaultCustomerLanguageCode = userInfo.defaultLanguageCode;
            this.defaultInterfaceLanguageCode = userInfo.defaultInterfaceLanguageCode;
            fixCookies();
            if (!RpcClass.csrfToken) {
                RpcClass.csrfToken = userInfo.csrfToken;
            }
            if (userInfo.isAdmin) {
                this.isAdmin = true;
                this.publicSessionId = userInfo.publicSessionId;
                this.login = "admin";
                this.applicationUser = {
                    login: 'admin'
                };
                EventBus.$emit(Events.userSignedIn, this.login);

            } else {
                try{
                    if(window['gtag'] && window.location.hostname === "silkplm.com") {
                        window['gtag']('set', 'user_data', {
                            "email": userInfo.applicationUser.email,
                            "address": {
                                "first_name": userInfo.applicationUser.firstName,
                                "last_name": userInfo.applicationUser.lastName
                            }
                        });
                    }
                } catch (e){
                    console.log("user-data-set failed")
                }

                this.publicSessionId = userInfo.publicSessionId;
                this.setUser(userInfo.applicationUser);
                this.setPermissions(userInfo.permissionCodes);
                this.featureFlags = userInfo.featureFlags;
                this.featureFlagsSet = {};
                this.systemLevelFeatureFlagsSet = {};
                this.modulesSet = {};
                for (const mod in ALL_MODULES) {
                    if (userInfo.modulesConfiguration[mod] || userInfo.modulesConfiguration[ALL_MODULES[mod]]) {
                        this.modulesSet[mod] = true;
                        this.modulesSet[ALL_MODULES[mod]] = true;
                    }
                }

                this.featureFlags.forEach(f => {
                    this.featureFlagsSet[f] = 1;
                    this.systemLevelFeatureFlagsSet[f] = 1;
                })
            }
        } else {

            this.publicSessionId = "";
            this.setUser(null);
            this.setPermissions(null);
            this.featureFlags = null;
            this.featureFlagsSet = {};
            this.modulesSet = {};
        }
    }

    resetFlags(userInfo) {
        this.featureFlags = userInfo.featureFlags;
        this.featureFlagsSet = {};
        this.systemLevelFeatureFlagsSet = {};

        this.featureFlags.forEach(f => {
            this.featureFlagsSet[f] = 1;
            this.systemLevelFeatureFlagsSet[f] = 1;
        })
    }

    hasFlag(flag) {
        return this.featureFlagsSet[flag] ? true : false;
    }

    _enablePermission(code) {
        let newCodes = _.clone(this.permissions);
        newCodes.push(code);
        this.setPermissions(newCodes);
    }

    _disablePermission(code) {
        this.setPermissions(_.without(this.permissions, code));
    }

    setPermissions(codes) {
        if (!codes) {
            if (!this.permissions || this.permissions.length === 0) {
                return;
            } else {
                this.permissions = [];
                this.permissionsByCode = {};
                this.permissionsByName = {};
                EventBus.$emit(Events.permissionsChanged, this.permissions);
                return;
            }
        }

        codes.sort();
        if (_.isEqual(codes, this.permissions)) {
            return;
        }

        this.permissions = codes;
        this.permissionsByCode = {};
        this.permissionsByName = {};
        codes.forEach((code) => {
            this.permissionsByCode[code] = 1;
        });
        for (let k in Perms) {
            let p = Perms[k]
            if (this.permissionsByCode[p.code]) {
                this.permissionsByName[p.name] = p;
                this.permissionsByCode[p.code] = p;
            }
        }

        EventBus.$emit(Events.permissionsChanged, this.permissions);
    }

    hasPermission(permission) {
        if (this.isAdmin) {
            return true;
        }
        if (!permission) {
            Log.warn("Unknown permission name:" + permission, true);
            return false;
        }
        let permName = _.isString(permission) ? permission : permission.name;
        if (!Perms.hasOwnProperty(permName)) {
            Log.warn("Unknown permission name:" + permName, true);
            return false;
        }


        let code = Perms[permName].code;
        if (this.permissionsByCode.hasOwnProperty(code)) {
            return true;
        }

        if (this.permissionsByCode.hasOwnProperty(Perms.SUPERUSER.code)) {
            return true;
        }

        if (code >= BUSINESS_OBJECTS_OFFSET) {
            const subType = code % (FORMS_SUB_CODE_MAX_VALUE + 1);
            if (subType === FORMS_READ_SUB_CODE || subType === FORMS_LIST_SUB_CODE) {  // or FIELDS_READ_SUB_CODE
                if (this.permissionsByCode.hasOwnProperty(Perms.ALL_BUSINESS_ENTITIES_READ.code)) {
                    return true;
                }
            }
            if (subType === FORMS_WRITE_SUB_CODE) { // or FIELDS_WRITE_SUB_CODE
                if (this.permissionsByCode.hasOwnProperty(Perms.ALL_BUSINESS_ENTITIES_WRITE.code)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * @param appUser {ApplicationUser}
     */
    setUser(appUser) {
        if (!appUser) {
            this.login = null;
            this.applicationUser = null;
            EventBus.$emit(Events.userSignedOut);
            return;
        }
        this.login = appUser.login;
        this.applicationUser = appUser;

        EventBus.$emit(Events.userSignedIn, this.login);
    }

    setRouter(router) {
        this.router = router;
    }

    async sleep(ms) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve()
            }, ms);
        });
    }

    isProdOrCert() {
        let hostname = window.location.hostname;
        return !(hostname === 'demo.silkplm.com' || hostname === 'localhost' || hostname === '127.0.0.1');
    }

    isProd() {
        return window.location.hostname === "silkplm.com"
    }

    isDev() {
        let hostname = window.location.hostname;
        if(hostname === 'localhost' || hostname === '127.0.0.1'){
            return true;
        }
        return false;
    }

    isDemo() {
        let hostname = window.location.hostname;
        if (hostname === 'demo.silkplm.com') {
            return true;
        }
        return false;
    }

    isCert() {
        let hostname = window.location.hostname;
        if (hostname === 'cert.silkplm.com') {
            return true;
        }
        return false;
    }

    async installRuntimeCode() {
        let lock = new InputLock();
        try {
            if (window['__rt_module'] !== undefined) {
                delete window['__rt_module'];
            }
            let script = document.createElement("script");
            script.setAttribute("type", "text/javascript");
            let prefix = "";

            if (RpcClass.baseAddress) {
                prefix = RpcClass.baseAddress;
            }
            let jsiURl = "";
            let loadJsiForSelenium = window['_loadJsiForSelenium'];
            if (window['_apiEndpoint']) {
                prefix = window['_apiEndpoint'];
                jsiURl = prefix + "/rpc/jsi?" + Math.random() + "-" + Date.now() +
                    "&x-token=" + RpcClass.csrfToken + "&internal-x-sess=" + encodeURIComponent(RpcClass.sessionCookie);
            } else if (window['isCordova']) {
                jsiURl= prefix + "/rpc/jsi?" + Math.random() + "-" + Date.now() +
                    "&x-token=" + RpcClass.csrfToken + "&internal-x-sess=" + encodeURIComponent(RpcClass.sessionCookie);
            } else {
                jsiURl = "/rpc/jsi?" + Math.random() + "-" + Date.now() + "&x-token=" + RpcClass.csrfToken;
            }
            script.setAttribute("src", jsiURl);
            window['_jsiUrl'] = jsiURl;

            if(loadJsiForSelenium){
                let xhr = new XMLHttpRequest();
                xhr.open('GET', jsiURl, true);
                xhr.onreadystatechange = function () {
                    if (xhr.readyState === 4) {
                        if (xhr.status === 200) {
                            window['_jsiBody'] = xhr.responseText;
                            Log.info(`JSI loaded`);
                        }
                    }
                };

                xhr.send();
            }


            document.getElementsByTagName("head")[0].appendChild(script);
            let cnt = 0;
            while (window['__rt_module'] === undefined) {
                cnt++;
                if (cnt < 10) {
                    await this.sleep(10);
                    continue;
                }
                if (cnt < 20) {
                    await this.sleep(20);
                    continue;
                }
                if (cnt < 30) {
                    await this.sleep(100);
                    continue;
                }
                await this.sleep(500);
                if (cnt >= 200) {
                    throw new Error("JSI download failed");
                }
            }
        } finally {
            lock.release();
        }
    }
}

const SessionManager = new _SessionManager();
window['SessionManager'] = SessionManager;
export default SessionManager;